import React, { useState, useEffect } from "react";
import { env } from "../../helpers/GlobalStateProvider";
import { useModalState } from "../../helpers/ModalProvider";
import { getApiUrl } from "../../config";
import axios from "axios";
import { Flex, Text } from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { Field } from "../../components/ui/field";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../components/ui/dialog";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { LuArrowRight, LuChevronDown } from "react-icons/lu";
import { savePrepaidKey } from "../../helpers/prepaidKeyManager";

const CreatePrepaidKeyModal: React.FC = () => {
  const { isCreatePrepaidModalVisible, setCreatePrepaidModalVisible, showPrepaidCreatedModal, showErrorMessage } =
    useModalState();
  const { address, status } = useAccount();
  const isWalletConnected = status === 'connected';
  const { openConnectModal } = useConnectModal();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isCreatePrepaidModalVisible) {
      setIsSubmitting(false);
    }
  }, [isCreatePrepaidModalVisible]);

  const handleCreate = async () => {
    if (!isWalletConnected || !address) {
      openConnectModal?.();
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `${getApiUrl(env)}/v3/prepaid/create`,
        {
          walletAddress: address,
        }
      );

      const apiKey = response.data?.apiKey || response.data?.key || "";
      const credits = response.data?.credits || 0;

      // Save via prepaidKeyManager (sessionStorage, obfuscated)
      savePrepaidKey({
        key: apiKey,
        creditsUsed: 0,
        creditsAvailable: credits,
        lastUsed: "",
        createdAt: new Date().toISOString(),
      });
      // Note: savePrepaidKey already dispatches "prepaidKeysUpdated"

      setCreatePrepaidModalVisible(false);

      showPrepaidCreatedModal({
        apiKey: apiKey || "—",
        credits,
      });
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') console.error("Error creating prepaid key:", error);
      showErrorMessage("Failed to create prepaid key. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const walletDisplay = isWalletConnected && address
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : "No wallet connected";

  return (
    <DialogRoot
      lazyMount
      open={isCreatePrepaidModalVisible}
      onOpenChange={(e) => setCreatePrepaidModalVisible(e.open)}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create pre-paid API key</DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody>
          <Flex direction="column" gap={5}>
            <Text fontSize="sm" color="fg.muted">
              Create a pre-paid API key and add credits at any time via x402 USDC payment.
            </Text>

            {/* Pay From (Wallet) */}
            <Field label="Pay From (Wallet)">
              <Flex
                bg="#f4f4f5"
                borderRadius="lg"
                height="40px"
                px={3}
                justifyContent="space-between"
                alignItems="center"
                cursor="pointer"
                onClick={() => {
                  if (!isWalletConnected) openConnectModal?.();
                }}
              >
                <Text fontSize="sm">
                  {isWalletConnected ? `Connected Wallet (${walletDisplay})` : "Connect Wallet"}
                </Text>
                <LuChevronDown size={16} />
              </Flex>
            </Field>
          </Flex>
        </DialogBody>

        <DialogFooter>
          <Button
            variant="solid"
            colorPalette="gray"
            width="full"
            onClick={handleCreate}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Prepaid Key"}
            {!isSubmitting && <LuArrowRight />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default CreatePrepaidKeyModal;
